// src/pages/DirectorInboxTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import "./GovInboxTab.css";
import "./StaffInboxTab.css";

import ApplicationFullViewModal from "../components/gos/ApplicationFullViewModal";

/* ─── pure helpers ──────────────────────────────────────────── */
const pick = (obj, key, fallback = "") => (obj && obj[key] != null ? obj[key] : fallback);

const normalizePayload = (res) => {
  const p = res?.data ?? {};
  if (Array.isArray(p)) return { items: p, meta: null };
  return { items: Array.isArray(p.data) ? p.data : [], meta: p.meta || null };
};

const norm = (v) => String(v || "").trim().toLowerCase();

const dateOnly = (v) => (v ? String(v).slice(0, 10) : "—");

const localizedName = (v, locale = "kz", fallback = "—") => {
  if (v == null || v === "") return fallback;
  if (typeof v === "string" || typeof v === "number") return String(v);

  const lang = locale === "kz" ? "kz" : "ru";

  return (
    v[`name_${lang}`] ||
    v.name ||
    v.title ||
    v.label ||
    fallback
  );
};

const getRegistrationNumber = (v) =>
  v?.registration_number ||
  v?.group_registration_number ||
  v?.application_group_registration_number ||
  v?.group?.registration_number ||
  v?.application_group?.registration_number ||
  v?.applications?.find?.((a) => a?.registration_number)?.registration_number ||
  v?.applications?.find?.((a) => a?.group?.registration_number)?.group?.registration_number ||
  "";

const getSentAt = (v) =>
  v?.sent_at ||
  v?.group_sent_at ||
  v?.submitted_at ||
  v?.group?.sent_at ||
  v?.application_group?.sent_at ||
  null;

const isEmptyDecision = (decision) => {
  if (decision == null) return true;

  const d = String(decision).trim().toLowerCase();

  return (
    d === "" ||
    d === "null" ||
    d === "undefined" ||
    d === "none" ||
    d === "—" ||
    d === "-"
  );
};

export default function DirectorInboxTab({ locale = "kz" }) {
  const t = useMemo(() => {
    const kz = {
      title: "Директор бекітуі",
      refresh: "Жаңарту",
      empty: "Тізім бос",
      err: "Қате",
      searchPh: "Іздеу (топ атауы, баспа немесе тіркеу нөмірі)...",

      sortLabel: "Сұрыптау",
      perPage: "Саны",
      reset: "Таза",

      sortSentDesc: "Жаңадан жіберілгендер",
      sortSentAsc: "Бұрын жіберілгендер",
      sortTitleAsc: "Атауы: A → Я",
      sortTitleDesc: "Атауы: Я → A",

      page: "Бет",
      of: "із",
      prev: "Алдыңғы",
      next: "Келесі",

      publisher: "Баспасы",
      status: "Күйі",

      groupInfoTitle: "Топ туралы жалпы ақпарат",
      registration_number: "Тіркеу нөмірі",
      noRegistrationNumber: "Тіркеу нөмірі жоқ",

      subject: "Пән",
      grade: "Сынып",
      education_level: "Білім деңгейі",
      direction: "Бағыт",
      development_language: "Әзірлеу тілі",
      teaching_language: "Оқыту тілі",
      publication_type: "Басылым түрі",
      service_type: "Қызмет түрі",
      submission_year: "Өткізу жылы",
      sent_at: "Жіберілген күні",

      apps: "Өтінімдер",
      appCategory: "Санат",
      appStatus: "Күйі",
      appDecision: "Шешім",
      appDate: "Жіберілген күні",

      open: "Ашу",
      close: "Жабу",
      viewFull: "Толық көру",
      appModalTitle: "Өтінім туралы толық ақпарат",
      loading: "Жүктелуде...",

      approveGroup: "Топты бекіту",
      approved: "✅ Бекітілді",
      pendingDecisions: "⏳ Барлық шешімдер күтілуде",
      approveSuccess: "✅ Сәтті бекітілді!",
      approveErr: "❌ Бекіту кезінде қате орын алды",
      alreadyApproved: "❌ Бұл шешім бұрын бекітілген",
    };

    const ru = {
      title: "Утверждение директора",
      refresh: "Обновить",
      empty: "Список пуст",
      err: "Ошибка",
      searchPh: "Поиск (название группы, издательство или рег. номер)...",

      sortLabel: "Сортировка",
      perPage: "Количество",
      reset: "Сброс",

      sortSentDesc: "Сначала отправленные новые",
      sortSentAsc: "Сначала отправленные ранние",
      sortTitleAsc: "Название: A → Я",
      sortTitleDesc: "Название: Я → A",

      page: "Стр.",
      of: "из",
      prev: "Назад",
      next: "Вперёд",

      publisher: "Издательство",
      status: "Статус",

      groupInfoTitle: "Общая информация группы",
      registration_number: "Регистрационный номер",
      noRegistrationNumber: "Регистрационный номер отсутствует",

      subject: "Предмет",
      grade: "Класс",
      education_level: "Уровень образования",
      direction: "Направление",
      development_language: "Язык разработки",
      teaching_language: "Язык обучения",
      publication_type: "Тип издания",
      service_type: "Тип услуги",
      submission_year: "Год подачи",
      sent_at: "Дата отправки",

      apps: "Заявки",
      appCategory: "Категория",
      appStatus: "Статус",
      appDecision: "Решение",
      appDate: "Дата отправки",

      open: "Развернуть",
      close: "Свернуть",
      viewFull: "Подробнее",
      appModalTitle: "Полная информация по заявке",
      loading: "Загрузка...",

      approveGroup: "Утвердить группу",
      approved: "✅ Утверждено",
      pendingDecisions: "⏳ Ожидаются решения по всем заявкам",
      approveSuccess: "✅ Успешно утверждено!",
      approveErr: "❌ Ошибка при утверждении",
      alreadyApproved: "❌ Это решение уже утверждено",
    };

    return locale === "kz" ? kz : ru;
  }, [locale]);

  const langHeader = useMemo(() => (locale === "kz" ? "kk" : locale), [locale]);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Accept-Language": langHeader,
  }), [langHeader]);

  const [rawGroups, setRawGroups] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const LS_KEY = "director_expanded_groups";

  const [expandedIds, setExpandedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set((Array.isArray(arr) ? arr : []).map(String));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(expandedIds)));
    } catch {}
  }, [expandedIds]);

  const toggleExpand = useCallback((id) => {
    const sid = String(id);

    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  }, []);

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [sortBy, setSortBy] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const resetFilters = useCallback(() => {
    setSearch("");
    setSearchDebounced("");
    setSortBy("sent_at");
    setSortDir("desc");
    setPerPage(10);
    setPage(1);
  }, []);

  const mountedRef = useRef(true);

  const loadGroups = useCallback(async (opts = {}) => {
    const { soft = false } = opts;

    if (soft) setRefreshing(true);
    else setLoading(true);

    setErr("");

    try {
      const params = {
        page: 1,
        per_page: 100,
        sort_by: "sent_at",
        sort_dir: "desc",
        view_as: "head",
        _ts: Date.now(),
      };

      const res = await axios.get("/backoffice/applications/grouped", {
        headers,
        params,
      });

      const { items } = normalizePayload(res);

      if (mountedRef.current) {
        setRawGroups(items || []);
      }
    } catch (e) {
      if (mountedRef.current) {
        setRawGroups([]);
        setErr(e?.response?.data?.message || e?.message || t.err);
      }
    } finally {
      if (soft) setRefreshing(false);
      else setLoading(false);
    }
  }, [headers, t.err]);

  useEffect(() => {
    mountedRef.current = true;
    loadGroups({ soft: false });

    return () => {
      mountedRef.current = false;
    };
  }, [loadGroups]);

  /* ─── APPROVE GROUP — одна кнопка на всю группу ─── */
  const approveGroup = useCallback(async (groupId) => {
    setApprovingId(groupId);

    try {
      await axios.post(`/backoffice/groups/${groupId}/approve`, {}, { headers });
      alert(t.approveSuccess);
      loadGroups({ soft: true });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;

      if (status === 409) {
        alert(t.alreadyApproved);
      } else {
        alert(msg || t.approveErr);
      }
    } finally {
      setApprovingId(null);
    }
  }, [headers, loadGroups, t]);

  /* ─── helpers: проверки на уровне группы ─── */
  const allAppsHaveDecision = useCallback((apps) => {
    if (!apps.length) return false;

    return apps.every((a) => {
      const d = a.decision?.status ?? a.decision;
      return !isEmptyDecision(d);
    });
  }, []);

  const isGroupApproved = useCallback((apps) => {
    if (!apps.length) return false;

    return apps.every((a) => {
      const dirApproval = Array.isArray(a.approvals)
        ? a.approvals.find((ap) =>
            String(ap.role || "").toLowerCase().includes("director") ||
            String(ap.role_name || "").toLowerCase().includes("director")
          )
        : null;

      return dirApproval?.approved === 1 || dirApproval?.approved === true;
    });
  }, []);

  const STATUS_DICT = useMemo(() => ({
    submitted: { ru: "Отправлено", kz: "Жіберілді" },
    sent: { ru: "Отправлено", kz: "Жіберілді" },
    accepted: { ru: "Принято", kz: "Қабылданды" },
    approved: { ru: "Одобрено", kz: "Мақұлданды" },
    declined: { ru: "Отклонено", kz: "Қабылданбады" },
    rejected: { ru: "Отклонено", kz: "Қабылданбады" },
    draft: { ru: "Черновик", kz: "Жоба" },
    new: { ru: "Новая", kz: "Жаңа" },
    in_review: { ru: "На рассмотрении", kz: "Қаралуда" },
    returned: { ru: "Возвращено на доработку", kz: "Өңдеуге қайтарылды" },
    ondeu: { ru: "После доработки", kz: "Өңдеуден келген" },
  }), []);

  const statusNormKey = useCallback((raw) => {
    const s = norm(raw);

    if (!s || s === "-") return null;

    if (["submitted", "отправ", "жібер"].some((x) => s.includes(x))) return "submitted";
    if (["accepted", "принят", "қабылдан"].some((x) => s.includes(x))) return "accepted";
    if (["approved", "одобрен", "мақұл"].some((x) => s.includes(x))) return "approved";
    if (["declined", "rejected", "отклон", "қабылданба"].some((x) => s.includes(x))) return "declined";
    if (["draft", "чернов", "жоба"].some((x) => s.includes(x))) return "draft";
    if (["in_review", "review", "рассмотр", "қарал"].some((x) => s.includes(x))) return "in_review";

    if (
      s.includes("өңдеуден келген") ||
      s.includes("после доработ") ||
      s.includes("из доработ")
    ) {
      return "ondeu";
    }

    if (
      s.includes("өңдеуге қайтар") ||
      s.includes("қайтар") ||
      s.includes("возвращ") ||
      s.includes("returned") ||
      s.includes("back")
    ) {
      return "returned";
    }

    if (["new", "нов", "жаңа"].some((x) => s.includes(x))) return "new";

    return null;
  }, []);

  const statusLabel = useCallback((raw) => {
    const key = statusNormKey(raw);

    if (!key) return String(raw || "-");

    const row = STATUS_DICT[key];

    return locale === "kz"
      ? row?.kz || String(raw || "-")
      : row?.ru || String(raw || "-");
  }, [STATUS_DICT, locale, statusNormKey]);

  const statusKey = useCallback((raw) => {
    const key = statusNormKey(raw);

    if (!key) return "neutral";
    if (key === "accepted" || key === "approved") return "ok";
    if (key === "submitted" || key === "sent") return "sent";
    if (["draft", "new", "in_review", "returned", "ondeu"].includes(key)) return "warn";
    if (["declined", "rejected"].includes(key)) return "danger";

    return "neutral";
  }, [statusNormKey]);

  const statusBadgeClass = useCallback((raw) => {
    const k = statusKey(raw);

    if (k === "ok") return "badge badge--ok";
    if (k === "sent") return "badge badge--sent";
    if (k === "warn") return "badge badge--warn";
    if (k === "danger") return "badge badge--danger";

    return "badge badge--neutral";
  }, [statusKey]);

  const formatDecision = useCallback((d) => {
    const value = d?.status ?? d;

    if (isEmptyDecision(value)) return "—";

    const v = String(value).toLowerCase().trim();

    if (v === "accept") return locale === "kz" ? "Қабылданды" : "Принято";
    if (v === "return") return locale === "kz" ? "Өңдеуге қайтарылды" : "Возвращено на доработку";
    if (v === "reject_expertise") return locale === "kz" ? "Сараптамадан бас тартылды" : "Отказано в экспертизе";

    return String(value);
  }, [locale]);

  const filtered = useMemo(() => {
    const q = norm(searchDebounced);
    let list = Array.isArray(rawGroups) ? [...rawGroups] : [];

    if (q) {
      list = list.filter((g) =>
        norm(g?.title).includes(q) ||
        norm(g?.publisher_name).includes(q) ||
        norm(g?.publisher?.organization_name).includes(q) ||
        norm(g?.publisher?.full_name).includes(q) ||
        norm(getRegistrationNumber(g)).includes(q)
      );
    }

    const getSortDate = (g) => String(getSentAt(g) || "");

    const cmpStr = (a, b) =>
      String(a || "").localeCompare(String(b || ""), "ru", { sensitivity: "base" });

    list.sort((a, b) => {
      const r = sortBy === "title"
        ? cmpStr(a?.title, b?.title)
        : cmpStr(getSortDate(a), getSortDate(b));

      return sortDir === "asc" ? r : -r;
    });

    return list;
  }, [rawGroups, searchDebounced, sortBy, sortDir]);

  const total = filtered.length;
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, perPage)));
  const pageSafe = Math.min(Math.max(1, page), lastPage);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
  }, [page, pageSafe]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, pageSafe, perPage]);

  const canPrev = pageSafe > 1;
  const canNext = pageSafe < lastPage;

  const gotoPage = useCallback((p) => {
    setPage(Math.min(Math.max(1, p), lastPage));
  }, [lastPage]);

  const getPageButtons = useCallback(() => {
    const pages = [];

    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (pageSafe > 4) {
      pages.push("left-ellipsis");
    } else {
      for (let i = 2; i <= 3; i++) pages.push(i);
    }

    const start = Math.max(pageSafe - 1, 4);
    const end = Math.min(pageSafe + 1, lastPage - 3);

    for (let i = start; i <= end; i++) pages.push(i);

    if (pageSafe < lastPage - 3) {
      pages.push("right-ellipsis");
    } else {
      for (let i = lastPage - 2; i < lastPage; i++) pages.push(i);
    }

    pages.push(lastPage);

    return pages;
  }, [lastPage, pageSafe]);

  const [appViewOpen, setAppViewOpen] = useState(false);
  const [appViewItem, setAppViewItem] = useState(null);

  const openAppView = useCallback((appItem) => {
    setAppViewItem(appItem);
    setAppViewOpen(true);
  }, []);

  const closeAppView = useCallback(() => {
    setAppViewOpen(false);
    setAppViewItem(null);
  }, []);

  if (loading) {
    return (
      <section className="gos-page">
        <h2 className="gos-h2">{t.title}</h2>

        <div className="gos-loading-card" aria-live="polite" aria-busy="true">
          <div className="gos-loading-row">
            <span className="gos-loading-spinner" />
            <span className="gos-loading-text">{t.loading}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="gos-page">
      <h2 className="gos-h2">{t.title}</h2>

      <div className="gl-filters">
        <input
          className="gl-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={t.searchPh}
        />

        <label className="gl-select">
          <span>{t.sortLabel}</span>

          <select
            value={`${sortBy}:${sortDir}`}
            onChange={(e) => {
              const [sb, sd] = e.target.value.split(":");
              setSortBy(sb);
              setSortDir(sd);
              setPage(1);
            }}
          >
            <option value="sent_at:desc">{t.sortSentDesc}</option>
            <option value="sent_at:asc">{t.sortSentAsc}</option>
            <option value="title:asc">{t.sortTitleAsc}</option>
            <option value="title:desc">{t.sortTitleDesc}</option>
          </select>
        </label>

        <label className="gl-select">
          <span>{t.perPage}</span>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value) || 10);
              setPage(1);
            }}
          >
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <button className="gl-btn gl-btn--ghost" onClick={resetFilters}>
          {t.reset}
        </button>

        <button
          className="gos-refresh-btn"
          onClick={() => loadGroups({ soft: true })}
          disabled={refreshing}
        >
          {refreshing ? t.loading : t.refresh}
        </button>
      </div>

      {err ? <div className="gl-error">{err}</div> : null}

      <ul className="gl-list">
        {!refreshing && paged.length === 0 ? (
          <li className="gl-item">
            <div className="gl-summary-wrap">
              <div className="gl-summary gl-summary--main">
                <div className="gl-head">
                  <div className="gl-title">{t.empty}</div>
                </div>
              </div>
            </div>
          </li>
        ) : null}

        {paged.map((g) => {
          const isOpen = expandedIds.has(String(g.id));
          const apps = Array.isArray(g.applications) ? g.applications : [];

          const groupStatusRaw = pick(g, "status", "-");
          const regNumber = getRegistrationNumber(g);
          const groupSentAt = getSentAt(g);
          const groupDate = groupSentAt ? dateOnly(groupSentAt) : "—";

          const pubName =
            g?.publisher_name ||
            g?.publisher?.organization_name ||
            g?.publisher?.full_name ||
            g?.publisher?.name ||
            "—";

          const groupApproved = isGroupApproved(apps);
          const canApprove = !groupApproved && allAppsHaveDecision(apps);
          const isApproving = approvingId === g.id;

          return (
            <li key={g.id} className={`gl-item ${isOpen ? "is-open" : ""}`}>
              <div className="gl-summary-wrap">
                <div
                  className="gl-summary gl-summary--main"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpand(g.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(g.id);
                    }
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="gl-head">
                    <div className="gl-title">{g.title || `#${g.id}`}</div>

                    <div className="gl-subtitle">
                      <span>{t.publisher}: {pubName}</span>

                      <span
                        className={`gl-pill gl-pill--code ${!regNumber ? "gl-pill--muted" : ""}`}
                        title={regNumber || t.noRegistrationNumber}
                      >
                        № {regNumber || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="gl-summary-meta">
                  <span
                    className={`group-status ${
                      statusKey(groupStatusRaw) === "sent" ? "status-sent" : "status-unsent"
                    }`}
                  >
                    {statusLabel(groupStatusRaw)}
                  </span>

                  <span className="gos-mini">{groupDate}</span>

                  {groupApproved ? (
                    <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 13 }}>
                      {t.approved}
                    </span>
                  ) : canApprove ? (
                    <button
                      type="button"
                      className="gl-btn"
                      style={{
                        background: "#1d4ed8",
                        color: "#fff",
                        border: "none",
                        padding: "5px 14px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 13,
                        opacity: isApproving ? 0.6 : 1,
                      }}
                      disabled={isApproving}
                      onClick={(e) => {
                        e.stopPropagation();
                        approveGroup(g.id);
                      }}
                    >
                      {isApproving ? "..." : t.approveGroup}
                    </button>
                  ) : (
                    <span style={{ opacity: 0.45, fontSize: 12 }}>
                      {t.pendingDecisions}
                    </span>
                  )}

                  <button
                    type="button"
                    className="gl-chevron-btn"
                    aria-label={isOpen ? t.close : t.open}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(g.id);
                    }}
                  >
                    <span className="gl-chevron" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="gl-details gl-details--scroll">
                  <div className="gl-ginfo">
                    <div className="gl-ginfo__head">
                      <div className="gl-ginfo__title">{t.groupInfoTitle}</div>
                    </div>

                    <div className="gl-ginfo__grid">
                      {[
                        [t.registration_number, regNumber || "—"],
                        [t.subject, localizedName(g?.subject, locale)],
                        [t.grade, localizedName(g?.grade, locale)],
                        [t.education_level, localizedName(g?.education_level || g?.educationLevel, locale)],
                        [t.direction, localizedName(g?.direction, locale)],
                        [t.development_language, localizedName(g?.development_language || g?.developmentLanguage, locale)],
                        [t.teaching_language, localizedName(g?.teaching_language || g?.teachingLanguage, locale)],
                        [t.publication_type, localizedName(g?.publication_type || g?.publicationType, locale)],
                        [t.service_type, localizedName(g?.service_type || g?.serviceType, locale)],
                        [t.submission_year, localizedName(g?.submission_year || g?.submissionYear, locale)],
                        [t.sent_at, groupDate],
                      ].map(([label, value]) => (
                        <div key={label} className="gl-ginfo__item">
                          <div className="gl-ginfo__label">{label}</div>
                          <div className="gl-ginfo__value">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="apps-group__body">
                    <div className="gos-apps-title">
                      {t.apps} <span className="gos-count">{apps.length}</span>
                    </div>

                    {apps.length ? (
                      <table className="apps-table">
                        <thead>
                          <tr>
                            <th>{t.appCategory}</th>
                            <th>{t.appStatus}</th>
                            <th>{t.appDecision}</th>
                            <th>{t.registration_number}</th>
                            <th>{t.appDate}</th>
                            <th />
                          </tr>
                        </thead>

                        <tbody>
                          {apps.map((a) => {
                            const appSentAt = getSentAt(a) || groupSentAt;
                            const appRegNumber = getRegistrationNumber(a) || regNumber;

                            return (
                              <tr key={a.id}>
                                <td data-label={t.appCategory}>
                                  {localizedName(a?.category, locale, "-")}
                                </td>

                                <td data-label={t.appStatus}>
                                  <span
                                    className={statusBadgeClass(a.status)}
                                    title={String(a.status || "-")}
                                  >
                                    {statusLabel(a.status)}
                                  </span>
                                </td>

                                <td data-label={t.appDecision}>
                                  {formatDecision(a.decision?.status ?? a.decision)}
                                </td>

                                <td data-label={t.registration_number}>
                                  {appRegNumber || "—"}
                                </td>

                                <td data-label={t.appDate}>
                                  {dateOnly(appSentAt)}
                                </td>

                                <td>
                                  <button
                                    type="button"
                                    className="gl-btn gl-btn--ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      openAppView({
                                        ...a,

                                        registration_number: appRegNumber,
                                        sent_at: appSentAt,

                                        group: {
                                          ...(a?.group || {}),
                                          id: g.id,
                                          title: g.title,
                                          registration_number: regNumber,
                                          sent_at: groupSentAt,

                                          subject: g.subject,
                                          grade: g.grade,
                                          education_level: g.education_level || g.educationLevel,
                                          direction: g.direction,
                                          development_language: g.development_language || g.developmentLanguage,
                                          teaching_language: g.teaching_language || g.teachingLanguage,
                                          publication_type: g.publication_type || g.publicationType,
                                          service_type: g.service_type || g.serviceType,
                                          submission_year: g.submission_year || g.submissionYear,
                                        },
                                      });
                                    }}
                                  >
                                    {t.viewFull}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="gos-muted">{t.empty}</div>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="gl-pagination">
        <button
          className="gl-page-btn"
          disabled={!canPrev}
          onClick={() => gotoPage(pageSafe - 1)}
        >
          ← {t.prev}
        </button>

        <div className="gl-page-numbers">
          {getPageButtons().map((p, idx) =>
            p === "left-ellipsis" || p === "right-ellipsis" ? (
              <span key={idx} className="gl-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`gl-page-btn ${p === pageSafe ? "is-active" : ""}`}
                onClick={() => gotoPage(p)}
                disabled={p === pageSafe}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          className="gl-page-btn"
          disabled={!canNext}
          onClick={() => gotoPage(pageSafe + 1)}
        >
          {t.next} →
        </button>

        <div className="gl-page-info">
          {t.page} {pageSafe} {t.of} {lastPage} • {total}
        </div>
      </div>

      <ApplicationFullViewModal
        open={appViewOpen}
        onClose={closeAppView}
        onChanged={() => loadGroups({ soft: true })}
        headers={headers}
        t={t}
        app={appViewItem}
        activeRole="director"
        endpoint="/backoffice/applications"
        locale={langHeader}
        embedded={true}
      />
    </section>
  );
}